import { textlikeDataTypes } from "@redotech/redo-flows-core-schema-common/type-system/data-types/base-types";
import { removeGenericFromType } from "@redotech/redo-flows-core-schema-common/type-system/data-types/generic-types";
import { SchemaField } from "@redotech/redo-flows-core-schema-common/type-system/schema";
import { variableDisplayName } from "@redotech/redo-flows-core-schema-common/type-system/variable-display";

/**
 * Whether a variable can be written into a template. Structured values (enums,
 * line items) render as something no merchant would want in their copy.
 */
export function isInsertableVariable(field: SchemaField): boolean {
  return (
    (textlikeDataTypes as readonly string[]).includes(
      removeGenericFromType(field.dataType),
    ) && !field.hideFromInsertionModal
  );
}

/**
 * How many of the braces right after the caret an editor closed on the author's
 * behalf, which the inserted token supplies itself.
 */
export function autoClosedBraceLength(textAfterCursor: string): number {
  return /^\}{1,2}/.exec(textAfterCursor)?.[0].length ?? 0;
}

interface InlineVariableQuery {
  startIndex: number;
  query: string;
}

function detectBraceQuery(
  textBeforeCursor: string,
): InlineVariableQuery | null {
  const openIndex = textBeforeCursor.lastIndexOf("{{");
  if (openIndex === -1) {
    return null;
  }
  const between = textBeforeCursor.slice(openIndex + 2);
  // A `}` (closing token) or newline means this `{{` is not an open token.
  if (/[}\n]/.test(between)) {
    return null;
  }
  // Only letters/digits/space/_/- can be part of an in-progress variable query.
  if (!/^[A-Za-z0-9 _-]*$/.test(between)) {
    return null;
  }
  return { startIndex: openIndex, query: between.trim() };
}

/** Spaces are excluded, unlike after `{{`, so an ordinary sentence containing
 * an `@` doesn't hold the menu — and Enter — open across words. */
function detectAtQuery(textBeforeCursor: string): InlineVariableQuery | null {
  const atIndex = textBeforeCursor.lastIndexOf("@");
  if (atIndex === -1) {
    return null;
  }
  // Mid-word `@` is an email address, not a trigger.
  if (atIndex > 0 && !/\s/.test(textBeforeCursor.charAt(atIndex - 1))) {
    return null;
  }
  const between = textBeforeCursor.slice(atIndex + 1);
  if (!/^[A-Za-z0-9_-]*$/.test(between)) {
    return null;
  }
  return { startIndex: atIndex, query: between };
}

export enum InlineVariableTrigger {
  BRACE = "brace",
  AT = "at",
}

const detectors: Record<
  InlineVariableTrigger,
  (textBeforeCursor: string) => InlineVariableQuery | null
> = {
  [InlineVariableTrigger.BRACE]: detectBraceQuery,
  [InlineVariableTrigger.AT]: detectAtQuery,
};

export const everyInlineVariableTrigger = Object.values(InlineVariableTrigger);

/**
 * Detect an in-progress variable token before the caret. The index returned is
 * what the caller replaces with the canonical token; the one nearest the caret
 * wins when a surface accepts more than one trigger.
 */
export function detectInlineVariableQuery(
  textBeforeCursor: string,
  triggers: readonly InlineVariableTrigger[] = everyInlineVariableTrigger,
): InlineVariableQuery | null {
  return triggers
    .map((trigger) => detectors[trigger](textBeforeCursor))
    .filter((query) => query != null)
    .reduce<InlineVariableQuery | null>(
      (nearest, query) =>
        nearest && nearest.startIndex > query.startIndex ? nearest : query,
      null,
    );
}

/** Whether a dynamic-variable field matches the user's typed query. */
export function matchesVariableQuery(
  field: SchemaField,
  query: string,
): boolean {
  if (!query) {
    return true;
  }
  const normalizedQuery = query.toLowerCase().replace(/[\s_-]/g, "");
  const key = field.key.toLowerCase();
  const title = variableDisplayName(field).toLowerCase().replace(/\s/g, "");
  return key.includes(normalizedQuery) || title.includes(normalizedQuery);
}
